/**
 * Pure query-shaping + bucket-mapping helpers for the remote/local docs search
 * aggregate path.
 *
 * Extracted from {@link DocsSearchService} so the contract (server aggregate
 * query shape, bounded fan-out options, bucket-to-result mapping) is testable
 * without constructing the framework-scoped Service. The Service delegates to
 * these; behaviour is unchanged.
 *
 * SOLID:
 * - S: Single Responsibility: only translates a query string into an aggregate
 *   request and maps aggregate buckets back into flat search results.
 * - D: the doc-title lookup is injected as a callback, decoupling the mapping
 *   from the DocsService / LiveData runtime.
 */
import {
  type AggregateInput,
  SearchQueryOccur,
  SearchQueryType,
} from '@affine/graphql';
import { truncate } from 'lodash-es';

import {
  classifyBlockSourceType,
  isSearchSourceType,
  type SearchSourceType,
} from './search-source';
import { getDocsSearchLocationIdentity } from './source-navigation';

export interface DocsSearchAggregateItem {
  docId: string;
  title: string;
  score: number;
  /**
   * sl-srag.3.1: which kind of object the hit came from, derived from the
   * block `flavour`. `doc` for title-only hits without a navigable block.
   */
  sourceType: SearchSourceType;
  blockId?: string;
  elementId?: string;
  parentBlockId?: string;
  parentFlavour?: string;
  noteBlockId?: string;
  displayMode?: string;
  blockContent?: string;
  databaseId?: string;
  databaseRowId?: string;
  databaseRowShardDocId?: string;
  databaseColumnId?: string;
  databaseTitle?: string;
  databaseRowLabel?: string;
  databaseColumnName?: string;
  visibility?: 'semantic' | 'technical';
  technicalSourceKind?: 'mail_storage';
}

export interface LocalDocTitleCandidate {
  docId: string;
  title: string;
  trash?: boolean;
}

export interface DocsSearchAggregateBucket {
  key: string;
  count: number;
  hits: {
    nodes: {
      fields: Record<string, unknown>;
      highlights?: Record<string, unknown> | null;
    }[];
  };
  score?: number;
}

const LOCAL_DOC_TITLE_MATCH_SCORE = 100;

export function mapLocalDocTitleMatches(
  query: string,
  candidates: readonly LocalDocTitleCandidate[]
): DocsSearchAggregateItem[] {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  if (!normalizedQuery) return [];

  return candidates.flatMap(candidate => {
    const title = candidate.title.trim();
    if (!title) return [];
    if (!title.toLocaleLowerCase().includes(normalizedQuery)) return [];

    return [
      {
        docId: candidate.docId,
        title: markFirstQueryMatch(title, query) ?? title,
        score: LOCAL_DOC_TITLE_MATCH_SCORE,
        sourceType: 'doc' as const,
      },
    ];
  });
}

export function mergeDocsSearchAggregateItems(
  primary: readonly DocsSearchAggregateItem[],
  secondary: readonly DocsSearchAggregateItem[]
): DocsSearchAggregateItem[] {
  const byLocation = new Map<string, DocsSearchAggregateItem>();

  for (const item of [...primary, ...secondary]) {
    const key = docsSearchAggregateItemLocationKey(item);
    const existing = byLocation.get(key);
    if (!existing) {
      byLocation.set(key, item);
      continue;
    }

    byLocation.set(key, {
      ...existing,
      ...item,
      title: existing.title || item.title,
      blockContent: existing.blockContent || item.blockContent,
      databaseTitle: existing.databaseTitle || item.databaseTitle,
      databaseRowLabel: existing.databaseRowLabel || item.databaseRowLabel,
      databaseColumnName:
        existing.databaseColumnName || item.databaseColumnName,
      visibility: existing.visibility ?? item.visibility,
      technicalSourceKind:
        existing.technicalSourceKind ?? item.technicalSourceKind,
      score: Math.max(existing.score, item.score),
    });
  }

  return [...byLocation.values()].sort((a, b) => b.score - a.score);
}

function docsSearchAggregateItemLocationKey(item: DocsSearchAggregateItem) {
  return getDocsSearchLocationIdentity(item);
}

export type DocsSearchAggregateOptions = {
  pagination: {
    limit: number;
    skip: number;
  };
  includeTechnicalSources?: boolean;
  hits: {
    pagination: {
      limit: number;
      skip: number;
    };
    fields: [
      'blockId',
      'flavour',
      'sourceType',
      'content',
      'parentBlockId',
      'parentFlavour',
      'additional',
    ];
    highlights: [
      {
        field: 'content';
        before: string;
        end: string;
      },
    ];
  };
};

/**
 * Bounded aggregate fan-out: at most 50 doc buckets, 5 block hits per bucket.
 * Keeps a large result set from being rendered unbounded.
 */
export function createDocsSearchAggregateOptions(): DocsSearchAggregateOptions {
  return {
    pagination: {
      limit: 50,
      skip: 0,
    },
    hits: {
      pagination: {
        limit: 5,
        skip: 0,
      },
      fields: [
        'blockId',
        'flavour',
        'sourceType',
        'content',
        'parentBlockId',
        'parentFlavour',
        'additional',
      ],
      highlights: [
        {
          field: 'content',
          before: '<b>',
          end: '</b>',
        },
      ],
    },
  };
}

/**
 * Server-side aggregate query: match content, OR-boosted by an affine:page
 * (title) match so documents whose title matches rank higher.
 */
export function createServerAggregateQuery(
  query: string
): AggregateInput['query'] {
  return {
    type: SearchQueryType.boolean,
    occur: SearchQueryOccur.must,
    queries: [
      {
        type: SearchQueryType.match,
        field: 'content',
        match: query,
      },
      {
        type: SearchQueryType.boolean,
        occur: SearchQueryOccur.should,
        queries: [
          {
            type: SearchQueryType.match,
            field: 'content',
            match: query,
          },
          {
            type: SearchQueryType.boost,
            boost: 1.5,
            query: {
              type: SearchQueryType.match,
              field: 'flavour',
              match: 'affine:page',
            },
          },
        ],
      },
    ],
  };
}

/**
 * Map aggregate buckets into flat result items.
 *
 * - affine:page hits (title-only matches) are skipped as block results.
 * - When a bucket only produced affine:page hits, a single title-level result
 *   is emitted without a blockId, titled by the first hit's highlight or the
 *   doc title.
 * - The score is the bucket score (then its count, then 1).
 */
export function mapDocsSearchAggregateBuckets(
  buckets: DocsSearchAggregateBucket[],
  query: string,
  getDocTitle: (docId: string) => string
): DocsSearchAggregateItem[] {
  const result: DocsSearchAggregateItem[] = [];

  for (const bucket of buckets) {
    const docTitle = getDocTitle(bucket.key);
    const bucketScore =
      typeof bucket.score === 'number' ? bucket.score : bucket.count || 1;
    const resultCountBeforeBucket = result.length;

    const surfaceFallbackNodes: DocsSearchAggregateBucket['hits']['nodes'] = [];
    let sawNavigableBlockHit = false;

    const navigableNodes = bucket.hits.nodes.filter(node => {
      const flavour = getFirstStringField(node.fields.flavour);
      if (flavour !== 'affine:note') return true;

      const noteId = getFirstStringField(node.fields.blockId);
      if (!noteId) return true;

      return !bucket.hits.nodes.some(candidate => {
        if (candidate === node) return false;
        const metadata = getSearchHitMetadata(candidate.fields);
        return (
          metadata.noteBlockId === noteId ||
          (metadata.parentFlavour === 'affine:note' &&
            metadata.parentBlockId === noteId)
        );
      });
    });

    for (const node of navigableNodes) {
      const flavour = getFirstStringField(node.fields.flavour);
      const sourceType =
        getSearchSourceType(node.fields.sourceType) ??
        classifyBlockSourceType(flavour);
      // Skip affine:page hits (title-only match, no navigable block)
      if (flavour === 'affine:page') continue;
      // affine:surface is the structural canvas container. Keep it only as a
      // fallback for older index data that has no per-element edgeless hits.
      if (flavour === 'affine:surface') {
        surfaceFallbackNodes.push(node);
        continue;
      }
      sawNavigableBlockHit = true;

      const blockId = getFirstStringField(node.fields.blockId);
      const elementId = getEdgelessElementId(flavour, blockId);
      const metadata = getSearchHitMetadata(node.fields);
      const blockContent = createBlockSnippet(node, query, sourceType);
      if (sourceType === 'image' && !blockContent) {
        continue;
      }
      result.push({
        docId: bucket.key,
        title: docTitle,
        blockId,
        elementId,
        ...metadata,
        score: bucketScore,
        sourceType,
        blockContent,
      });
    }

    if (
      result.length === resultCountBeforeBucket &&
      surfaceFallbackNodes.length > 0
    ) {
      for (const node of surfaceFallbackNodes) {
        const blockId = getFirstStringField(node.fields.blockId);
        result.push({
          docId: bucket.key,
          title: docTitle,
          blockId,
          ...getSearchHitMetadata(node.fields),
          score: bucketScore,
          sourceType:
            getSearchSourceType(node.fields.sourceType) ??
            classifyBlockSourceType('affine:surface'),
          blockContent: createBlockSnippet(node, query, 'edgeless'),
        });
      }
    }

    // If only affine:page hits matched (title-only), add one result without blockId.
    if (result.length === resultCountBeforeBucket && !sawNavigableBlockHit) {
      const firstHit = bucket.hits.nodes[0];
      result.push({
        docId: bucket.key,
        title: firstHit?.highlights
          ? (getFirstHighlight(firstHit.highlights, 'content', query) ??
            docTitle)
          : docTitle,
        score: bucketScore,
        sourceType: 'doc',
      });
    }
  }

  return result;
}

function createBlockSnippet(
  node: {
    fields: Record<string, unknown>;
    highlights?: Record<string, unknown> | null;
  },
  query: string,
  sourceType: SearchSourceType
) {
  const highlighted = node.highlights
    ? getFirstHighlight(node.highlights, 'content', query)
    : undefined;
  if (sourceType === 'database') {
    return markFirstQueryMatch(
      normalizeDatabaseSnippet(
        highlighted ?? getBestStringField(node.fields.content, query)
      ),
      query
    );
  }

  if (sourceType === 'image') {
    return createImageSnippet(
      getBestStringField(node.fields.content, query),
      highlighted,
      query
    );
  }

  if (highlighted?.includes('<b>')) {
    return highlighted;
  }

  const content = getBestStringField(node.fields.content, query);
  const focused = createQueryFocusedSnippet(content, query);
  if (focused) return focused;

  return highlighted ?? truncate(content, { length: 220 });
}

function createImageSnippet(
  content: string,
  highlighted: string | undefined,
  query: string
) {
  if (highlighted?.includes('<b>')) {
    return highlighted;
  }

  const sections = splitImageIndexSections(content);
  for (const section of sections) {
    const focused = createQueryFocusedSnippet(section, query);
    if (focused) return focused;
  }

  if (highlighted) {
    const markedHighlight = markFirstQueryMatch(highlighted, query);
    if (markedHighlight?.includes('<b>')) return markedHighlight;
  }

  const focusedContent = createQueryFocusedSnippet(content, query);
  if (focusedContent) return focusedContent;

  // Image hits must explain their provenance. A generic image OCR/vision
  // truncation without the matched term is not actionable in search results.
  return undefined;
}

function splitImageIndexSections(content: string) {
  return content
    .split(/\n{2,}/)
    .map(section => section.trim())
    .filter(Boolean);
}

function createQueryFocusedSnippet(content: string, query: string) {
  const match = findBestQueryMatch(content, query);
  if (!match) return undefined;

  const start = Math.max(0, match.index - 80);
  const end = Math.min(content.length, match.index + match.length + 120);
  const snippet = content.slice(start, end);
  return `${start > 0 ? '...' : ''}${markQueryAt(
    snippet,
    match.index - start,
    match.length
  )}${end < content.length ? '...' : ''}`;
}

function normalizeDatabaseSnippet(content: string) {
  const trimmed = content.trim();
  const markdownLink = trimmed.match(
    /^\[(?:database\s*[·:]\s*)?(.+?)\]\[\]\([^)]+\)$/i
  );
  if (markdownLink) return markdownLink[1].trim();

  const bracketed = trimmed.match(/^\[(?:Database:\s*)?(.+?)\]$/i);
  if (bracketed) return bracketed[1].trim();

  return trimmed.replace(/^Database:\s*/i, '');
}

export function markFirstQueryMatch(
  content: string | undefined,
  query: string
) {
  if (!content) return content;
  const normalizedQuery = query.trim();
  if (!normalizedQuery) return content;
  if (content.includes('<b>')) return content;

  const match = findBestQueryMatch(content, normalizedQuery);
  if (!match) return content;
  return markQueryAt(content, match.index, match.length);
}

function findBestQueryMatch(content: string, query: string) {
  const normalizedQuery = query.trim();
  if (!normalizedQuery) return null;

  const lowerContent = content.toLowerCase();
  const lowerQuery = normalizedQuery.toLowerCase();
  const exactIndex = lowerContent.indexOf(lowerQuery);
  if (exactIndex >= 0) {
    return { index: exactIndex, length: normalizedQuery.length };
  }

  const terms = normalizedQuery
    .split(/\s+/)
    .map(term => term.trim())
    .filter(term => term.length >= 2)
    .sort((a, b) => b.length - a.length);

  for (const term of terms) {
    const index = lowerContent.indexOf(term.toLowerCase());
    if (index >= 0) return { index, length: term.length };
  }

  return null;
}

function markQueryAt(content: string, index: number, length: number) {
  if (index < 0 || length <= 0) return content;
  return `${content.slice(0, index)}<b>${content.slice(
    index,
    index + length
  )}</b>${content.slice(index + length)}`;
}

function getSearchSourceType(value: unknown): SearchSourceType | undefined {
  const sourceType = getFirstStringField(value);
  return isSearchSourceType(sourceType) ? sourceType : undefined;
}

function getFirstStringField(value: unknown) {
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  return '';
}

function getSearchHitMetadata(fields: Record<string, unknown>) {
  const additional = getAdditionalObject(fields.additional);
  const noteBlockId = getStringProperty(additional, 'noteBlockId');
  const displayMode = getStringProperty(additional, 'displayMode');
  const parentBlockId = getFirstStringField(fields.parentBlockId);
  const parentFlavour = getFirstStringField(fields.parentFlavour);

  return {
    ...(parentBlockId ? { parentBlockId } : {}),
    ...(parentFlavour ? { parentFlavour } : {}),
    ...(noteBlockId ? { noteBlockId } : {}),
    ...(displayMode ? { displayMode } : {}),
  };
}

function getAdditionalObject(value: unknown): Record<string, unknown> | null {
  const raw = Array.isArray(value) ? value[0] : value;
  if (!raw) return null;
  if (typeof raw === 'object') return raw as Record<string, unknown>;
  if (typeof raw !== 'string') return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function getStringProperty(
  value: Record<string, unknown> | null,
  property: string
) {
  const raw = value?.[property];
  return typeof raw === 'string' ? raw : undefined;
}

function getBestStringField(value: unknown, query: string) {
  if (typeof value === 'string') return value;
  if (!Array.isArray(value)) return '';

  const strings = value.filter(
    (item): item is string => typeof item === 'string'
  );
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return strings[0] ?? '';

  return (
    strings.find(item => item.toLowerCase().includes(normalizedQuery)) ??
    strings[0] ??
    ''
  );
}

function getEdgelessElementId(flavour: string | undefined, blockId: string) {
  if (!blockId || !flavour?.startsWith('edgeless:')) return undefined;
  if (flavour === 'edgeless:mindmap-node') return undefined;
  return blockId;
}

function getFirstHighlight(
  highlights: Record<string, unknown>,
  field: string,
  query: string
) {
  const value = highlights[field];
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const strings = value.filter(
      (item): item is string => typeof item === 'string'
    );
    const normalizedQuery = query.trim().toLowerCase();
    return (
      strings.find(item => item.includes('<b>')) ??
      strings.find(item => item.toLowerCase().includes(normalizedQuery)) ??
      strings[0]
    );
  }
  return undefined;
}
